import React from 'react';
import './App.scss';
import { createBrowserRouter, createRoutesFromElements, defer, Navigate, Route, useLocation } from 'react-router-dom';
import HomePage from './pages/home/home.page';
import GlobalLayout from './components/layouts/global/global.layout';
import { AuthLayout } from './components/auth/auth.layout';
import ResetPasswordPage from './pages/user/resetPassword/resetPassword.page';
import { useAuth } from './hooks/use-auth.hook';
import LoginAsPage from './pages/admin/login-as/login-as.page';
import ConnectionsPage from './pages/admin/connections/connections.page';
import PageAdminPage from './pages/admin/page-admin.page';
import PageAdminUpdatePage from './pages/admin/page-admin-update.page';
import ForgotPasswordPage from './pages/user/forgotPassword/forgotPassword.page';
import ConfirmEmailChangePage from './pages/user/confirmEmailChange/confirmEmailChange.page';
import AccountPage from './pages/account/account.page';
import ContactsPage from './pages/contacts/contacts.page';
import MandatePage from './pages/mandate/mandate.page';
import TechnicalIndicatorsPdfPage from './pages/technical-indicators/technical-indicators-pdf.page';
import { isAdminUser } from './utils/access-role.util';
import NotFoundPage from './pages/not-found/not-found.page';

/**
 * Récupération des données de l'utilisateur depuis le stockage local
 */
const getUserData = () =>
    new Promise(resolve => {
        const user = globalThis.localStorage.getItem('user');
        resolve(user);
    });

/**
 * Initialisation des routes web
 * Les balises <Route> associe l'url à la page
 */
export const App = createBrowserRouter(
    createRoutesFromElements(
        <Route element={<AuthLayout />} loader={() => defer({ userPromise: getUserData() })}>
            <Route element={<GlobalLayout />}>
                <Route path="/" element={<HomePage />} />
                {/*Page compte*/}
                <Route
                    path="/compte"
                    element={
                        <CheckAuth>
                            <AccountPage />
                        </CheckAuth>
                    }
                />
                {/*Page contact*/}
                <Route
                    path="/contacts"
                    element={
                        <CheckAuth>
                            <ContactsPage />
                        </CheckAuth>
                    }
                />
                {/*Page changement de mot de passe*/}
                <Route
                    path="/changement-mot-de-passe"
                    element={
                        <CheckAuth>
                            <ResetPasswordPage />
                        </CheckAuth>
                    }
                />
                {/*Page assemblée générale*/}
                <Route
                    path="/elu/ag"
                    element={
                        <CheckAuth>
                            <MandatePage />
                        </CheckAuth>
                    }
                />
                {/*Page Bureau Syndical et Conseil d'Administration*/}
                <Route
                    path="/elu/bsca"
                    element={
                        <CheckAuth>
                            <MandatePage />
                        </CheckAuth>
                    }
                />
                {/*Page Cope eau*/}
                <Route
                    path="/elu/eau"
                    element={
                        <CheckAuth>
                            <MandatePage />
                        </CheckAuth>
                    }
                />
                {/*Page cope assainissement*/}
                <Route
                    path="/elu/assainissement"
                    element={
                        <CheckAuth>
                            <MandatePage />
                        </CheckAuth>
                    }
                />
                {/*Page bassin*/}
                <Route
                    path="/elu/bassin"
                    element={
                        <CheckAuth>
                            <MandatePage />
                        </CheckAuth>
                    }
                />
                {/*Page territoire*/}
                <Route
                    path="/elu/territoire"
                    element={
                        <CheckAuth>
                            <MandatePage />
                        </CheckAuth>
                    }
                />
                {/*Page commissioins*/}
                <Route
                    path="/elu/commissions"
                    element={
                        <CheckAuth>
                            <MandatePage />
                        </CheckAuth>
                    }
                />
                {/* Page de génération d'un PDF d'indicateur technique */}
                <Route
                    path="/elu/generer-pdf"
                    element={
                        <CheckAuth>
                            <TechnicalIndicatorsPdfPage />
                        </CheckAuth>
                    }
                />
                {/*Page admin se connecter en tant que*/}
                <Route
                    path="/administration/se-connecter-en-tant-que"
                    element={
                        <CheckAdminAccess>
                            <LoginAsPage />
                        </CheckAdminAccess>
                    }
                />
                {/*Page admin statistiques de connexion*/}
                <Route
                    path="/administration/connexions"
                    element={
                        <CheckAdminAccess>
                            <ConnectionsPage />
                        </CheckAdminAccess>
                    }
                />
                {/*Page mentions légales*/}
                <Route path="/mentions-legales" element={<PageAdminPage pageName="mentions-legales" />} />
                {/*Page modification mentions légales*/}
                <Route
                    path="/modifier-mentions-legales"
                    element={
                        <CheckAdminAccess>
                            <PageAdminUpdatePage pageName="mentions-legales" />
                        </CheckAdminAccess>
                    }
                />
                {/*Page politique de confidentialité*/}
                <Route
                    path="/politique-confidentialite"
                    element={<PageAdminPage pageName="politique-confidentialite" />}
                />
                {/*Page modification politique de confidentialité*/}
                <Route
                    path="/modifier-politique-confidentialite"
                    element={
                        <CheckAdminAccess>
                            <PageAdminUpdatePage pageName="politique-confidentialite" />
                        </CheckAdminAccess>
                    }
                />
                {/*Page demande changement MDP*/}
                <Route path="/reinitialisation-mot-de-passe" element={<ForgotPasswordPage />} />
                <Route path="/reinitialisation-mot-de-passe/confirmation" element={<ResetPasswordPage />} />
                {/*Page confirmation changement email*/}
                <Route path="/confirmation-changement-email" element={<ConfirmEmailChangePage />} />
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Route>
    )
);

/**
 * Fonction vérifiant si l'user peut accéder à la page donnée ou doit être redirigé en fonction de son état d'authentification
 * @param children Element qui sera retourné si l'état d'authentification est correct
 * @param logged Bool qui détermine si la page nécessite d'être connecté ou déconnecté
 * @constructor
 */
export function CheckAuth({ children }: Readonly<{ children: JSX.Element }>) {
    const auth = useAuth();
    const location = useLocation();

    if (!auth.user) {
        //On redirige si la personne est connectée
        return <Navigate to="/" state={{ from: location }} replace />;
    }
    if (auth?.user?.resetPassword && !location.pathname?.includes('changement-mot-de-passe')) {
        //On redirige si la personne est connectée
        return <Navigate to="/changement-mot-de-passe" state={{ from: location }} replace />;
    }

    return children;
}

export function CheckAdminAccess({ children }: Readonly<{ children: JSX.Element }>) {
    const auth = useAuth();
    const location = useLocation();

    if (!auth.user || auth.user.emailLoggedInAS || !isAdminUser(auth.user)) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    return children;
}
